import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Spinner } from '@helios/ui';
import { env } from '@/lib/env';
import {
  resolveProjectCity,
  validProjectCoordinates,
  withGeocodingTimeout,
  type ProjectCityLocation,
} from './googleProjectGeocoding';
import {
  GOOGLE_MAPS_AUTH_FAILURE,
  loadGoogleMaps,
  type GoogleMapInstance,
  type GoogleMapMarker,
  type GoogleMapsApi,
} from './googleMapsLoader';

export interface GoogleProjectLocation {
  id: string;
  latitude: number;
  longitude: number;
  title: string;
  riskLevel: 'CRITICAL' | 'HIGH' | 'MODERATE' | 'LOW' | null;
  pinColor?: string;
  /** Printed on the pin so it can be matched to a numbered project list. */
  pinLabel?: string;
  /** Pixels to shift the pin sideways so pins at a shared site do not cover each other. */
  pinOffsetX?: number;
}

const COLORS = { CRITICAL: '#ef4444', HIGH: '#f97316', MODERATE: '#eab308', LOW: '#10b981' };
/** Higher risk draws on top where pins overlap. */
const RISK_ORDER = { CRITICAL: 4, HIGH: 3, MODERATE: 2, LOW: 1 };
const DEFAULT_CENTER = { lat: 39.5, lng: -98.35 };

/** Displays only coordinates from the authenticated project API. No Google geocoding is stored. */
export function GoogleProjectMap({
  markers,
  onSelect,
  cities = [],
  configuration,
  className = 'h-[360px] sm:h-[420px] xl:h-[500px]',
}: {
  markers: GoogleProjectLocation[];
  onSelect: (id: string) => void;
  cities?: ProjectCityLocation[];
  configuration?: {
    enabled: boolean;
    browserKey?: string;
    mapId?: string;
    allowApproximateGeocoding: boolean;
  };
  className?: string;
}) {
  const container = useRef<HTMLDivElement>(null);
  const mapRef = useRef<GoogleMapInstance | null>(null);
  const sdkRef = useRef<GoogleMapsApi | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState('');
  const [generation, setGeneration] = useState(0);
  const [cityMarkers, setCityMarkers] = useState<GoogleProjectLocation[]>([]);
  const [cityIssues, setCityIssues] = useState<
    Array<{ id: string; title: string; reason: string }>
  >([]);
  const [locating, setLocating] = useState(false);
  const geocodeRun = useRef(0);
  const browserKey = configuration ? configuration.browserKey : env.googleMapsBrowserKey?.trim();
  const mapId = configuration ? configuration.mapId : env.googleMapsMapId?.trim();
  const configured = Boolean(browserKey);
  const cityIdentity = JSON.stringify(
    cities.map((city) => [city.id, city.city, city.state, city.riskLevel]),
  );
  const validMarkers = useMemo(
    () =>
      [...markers, ...cityMarkers].filter((marker) =>
        validProjectCoordinates(marker.latitude, marker.longitude),
      ),
    [markers, cityMarkers],
  );

  useEffect(
    () => () => {
      geocodeRun.current += 1;
    },
    [],
  );
  useEffect(() => {
    geocodeRun.current += 1;
    setCityMarkers([]);
    setCityIssues([]);
    setLocating(false);
  }, [cityIdentity]);

  const locateCities = async () => {
    const sdk = sdkRef.current;
    if (!sdk || state !== 'ready') return;
    const run = ++geocodeRun.current;
    setLocating(true);
    setCityMarkers([]);
    setCityIssues([]);
    const resolved: GoogleProjectLocation[] = [];
    const issues: Array<{ id: string; title: string; reason: string }> = [];
    const groups = new Map<string, ProjectCityLocation[]>();
    for (const city of cities) {
      if (!city.city?.trim() || !city.state?.trim()) {
        issues.push({
          id: city.id,
          title: city.title,
          reason: 'City and state are required in the project record.',
        });
      } else {
        const key = `${city.city.trim().toLowerCase()}|${city.state.trim().toLowerCase()}`;
        groups.set(key, [...(groups.get(key) ?? []), city]);
      }
    }
    let blockedReason: string | undefined;
    for (const group of groups.values()) {
      if (run !== geocodeRun.current) return;
      const project = group[0];
      try {
        if (blockedReason) throw new Error(blockedReason);
        const response = await withGeocodingTimeout(
          new sdk.Geocoder().geocode({ address: `${project.city}, ${project.state}` }),
        );
        if (run !== geocodeRun.current) return;
        const location = resolveProjectCity(project.city!, project.state!, response.results);
        if (!location) {
          issues.push(
            ...group.map((item) => ({
              id: item.id,
              title: item.title,
              reason: 'No unique, exact city and state match. Check the project location.',
            })),
          );
        } else {
          resolved.push(
            ...group.map((item) => ({
              id: item.id,
              title: `${item.title} · Approximate city location, not a verified job site`,
              riskLevel: item.riskLevel,
              pinColor: item.pinColor,
              ...location,
            })),
          );
        }
      } catch (cause: unknown) {
        const code =
          typeof cause === 'object' && cause !== null && 'code' in cause ? String(cause.code) : '';
        if (code === 'OVER_QUERY_LIMIT' || code === 'REQUEST_DENIED')
          blockedReason =
            'Google geocoding is unavailable or quota-limited. Check Geocoding API activation, key restrictions, and billing.';
        issues.push(
          ...group.map((item) => ({
            id: item.id,
            title: item.title,
            reason:
              blockedReason ??
              'Google could not resolve this city. Check its city and state and retry.',
          })),
        );
      }
      if (run !== geocodeRun.current) return;
      setCityMarkers([...resolved]);
      setCityIssues([...issues]);
    }
    if (run !== geocodeRun.current) return;
    setCityIssues(issues);
    setLocating(false);
  };

  useEffect(() => {
    if (!browserKey || !container.current) return;
    let disposed = false;
    let tileListener: { remove(): void } | undefined;
    let tileTimeout: number | undefined;
    const showError = (message: string) => {
      if (disposed) return;
      window.clearTimeout(tileTimeout);
      setError(message);
      setState('error');
    };
    const authFailure = () =>
      showError(
        'Google Maps rejected this browser. Ask an administrator to check the browser key restrictions, Maps JavaScript API, and billing, then reload this page.',
      );
    window.addEventListener(GOOGLE_MAPS_AUTH_FAILURE, authFailure);
    setState('loading');
    setError('');
    void loadGoogleMaps(browserKey)
      .then((sdk) => {
        if (disposed || !container.current) return;
        sdkRef.current = sdk;
        const map = new sdk.Map(container.current, {
          center: DEFAULT_CENTER,
          zoom: 4,
          minZoom: 2,
          maxZoom: 20,
          mapId,
          colorScheme: 'DARK',
          streetViewControl: false,
          mapTypeControl: false,
          gestureHandling: 'cooperative',
        });
        mapRef.current = map;
        tileListener = map.addListener('tilesloaded', () => {
          if (!disposed) {
            window.clearTimeout(tileTimeout);
            setState((current) => (current === 'error' ? current : 'ready'));
          }
        });
        tileTimeout = window.setTimeout(
          () =>
            showError(
              'Google Maps tiles did not load. Check connectivity, key restrictions, billing, and the configured map ID, then retry.',
            ),
          20_000,
        );
        setGeneration((value) => value + 1);
      })
      .catch((cause: unknown) => {
        showError(cause instanceof Error ? cause.message : 'Google Maps could not load.');
      });
    return () => {
      disposed = true;
      window.clearTimeout(tileTimeout);
      window.removeEventListener(GOOGLE_MAPS_AUTH_FAILURE, authFailure);
      tileListener?.remove();
      if (mapRef.current) sdkRef.current?.event.clearInstanceListeners(mapRef.current);
      mapRef.current = null;
    };
  }, [attempt, browserKey, mapId]);

  useEffect(() => {
    const sdk = sdkRef.current;
    const map = mapRef.current;
    if (!sdk || !map) return;
    const attached: Array<{ marker: GoogleMapMarker; click: () => void }> = [];
    const classic: Array<{
      marker: { setMap(map: GoogleMapInstance | null): void };
      listener: { remove(): void };
    }> = [];
    try {
      const bounds = new sdk.LatLngBounds();
      for (const project of validMarkers) {
        const position = { lat: project.latitude, lng: project.longitude };
        if (!mapId) {
          // Classic Marker remains supported by Google; migrate automatically when a real map ID is configured.
          const marker = new sdk.Marker({
            map,
            position,
            title: project.title,
            optimized: false,
            zIndex: project.riskLevel ? RISK_ORDER[project.riskLevel] : 0,
            ...(project.pinLabel
              ? {
                  label: {
                    text: project.pinLabel,
                    color: '#0b0f14',
                    fontSize: '11px',
                    fontWeight: '700',
                  },
                }
              : {}),
            icon: {
              path: 'M 0,-8 A 8,8 0 1,1 0,8 A 8,8 0 1,1 0,-8',
              fillColor:
                project.pinColor ?? (project.riskLevel ? COLORS[project.riskLevel] : '#94a3b8'),
              fillOpacity: 1,
              strokeColor: '#f8fafc',
              strokeWeight: 2,
              scale: project.pinLabel ? 1.45 : 1.2,
              // Symbol anchors are in path units, so divide the pixel offset by the scale.
              ...(project.pinOffsetX
                ? { anchor: new sdk.Point(-project.pinOffsetX / 1.45, 0) }
                : {}),
            },
          });
          classic.push({
            marker,
            listener: marker.addListener('click', () => onSelect(project.id)),
          });
          bounds.extend(position);
          continue;
        }
        const marker = new sdk.marker.AdvancedMarkerElement({
          map,
          position,
          title: project.title,
          gmpClickable: true,
        });
        const click = () => onSelect(project.id);
        attached.push({ marker, click });
        const pin = new sdk.marker.PinElement({
          background:
            project.pinColor ?? (project.riskLevel ? COLORS[project.riskLevel] : '#94a3b8'),
          borderColor: '#f8fafc',
          glyphColor: '#0b0f14',
          scale: 1.2,
          ...(project.pinLabel ? { glyph: project.pinLabel } : {}),
        });
        if (project.pinOffsetX) pin.style.transform = `translateX(${project.pinOffsetX}px)`;
        marker.append(pin);
        marker.addEventListener('gmp-click', click);
        bounds.extend(position);
      }
      // Generous padding keeps edge pins clear of the frame and the map controls.
      if (validMarkers.length > 1) map.fitBounds(bounds, 72);
      else {
        const project = validMarkers[0];
        map.setCenter(project ? { lat: project.latitude, lng: project.longitude } : DEFAULT_CENTER);
        map.setZoom(project ? 8 : 4);
      }
    } catch {
      setError(
        'Google Maps project markers could not render. Check the configured map ID and retry.',
      );
      setState('error');
    }
    return () => {
      for (const { marker, click } of attached) {
        marker.removeEventListener('gmp-click', click);
        marker.map = null;
      }
      for (const { marker, listener } of classic) {
        listener.remove();
        marker.setMap(null);
      }
    };
  }, [generation, mapId, onSelect, validMarkers]);

  return (
    <div className="min-w-0 space-y-2">
      <div
        className={`border-border bg-background relative overflow-hidden rounded-lg border ${className}`}
        role="region"
        aria-label="Interactive project location map"
      >
        <div ref={container} className="h-full w-full" data-testid="google-project-map" />
        {!configured ? (
          <div
            className="bg-background absolute inset-0 flex flex-col items-center justify-center gap-2 p-6 text-center"
            role="status"
          >
            <p className="text-sm font-medium">
              {configuration?.enabled === false
                ? 'Google Maps is disabled for this workspace'
                : 'Google Maps setup required'}
            </p>
            <p className="text-muted-foreground max-w-md text-xs">
              An administrator must configure a restricted Google Maps browser key. Project records
              remain available in the project list.
            </p>
          </div>
        ) : state === 'loading' ? (
          <div
            className="border-border bg-background/95 pointer-events-none absolute left-3 top-3 flex items-center gap-2 rounded-lg border p-3 text-sm"
            role="status"
          >
            <Spinner className="h-4 w-4" /> Loading Google Maps…
          </div>
        ) : state === 'error' ? (
          <div
            className="bg-background absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center"
            role="alert"
          >
            <p className="text-sm font-medium">Google Maps unavailable</p>
            <p className="text-muted-foreground max-w-md text-xs">{error}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                /authorization failed|configuration changed/i.test(error)
                  ? window.location.reload()
                  : setAttempt((value) => value + 1)
              }
            >
              {/authorization failed|configuration changed/i.test(error)
                ? 'Reload Google Maps'
                : 'Retry Google Maps'}
            </Button>
          </div>
        ) : null}
      </div>
      <p className="text-muted-foreground text-[11px]">
        Project coordinates: ConstructionOS project records · Basemap: Google Maps
        {markers.some((marker) => !validProjectCoordinates(marker.latitude, marker.longitude))
          ? ' · Invalid coordinates omitted'
          : ''}
      </p>
      {cities.length > 0 ? (
        <div className="border-border bg-background/50 space-y-2 rounded-lg border p-3">
          <p className="text-muted-foreground text-xs">
            {cities.length} projects have no verified coordinates. Stored city and state can be
            located approximately with Google; these are not job-site locations. Results remain only
            in this open map view.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={state !== 'ready' || !configured || locating}
            onClick={() => {
              void locateCities();
            }}
          >
            {locating ? 'Locating project cities…' : 'Locate project cities'}
          </Button>
          {cityMarkers.length > 0 ? (
            <p className="text-muted-foreground text-xs" role="status">
              {cityMarkers.length} approximate city locations · Source: Google Geocoding · Not saved
              to project records
            </p>
          ) : null}
          {cityIssues.length > 0 ? (
            <details className="text-xs">
              <summary className="text-muted-foreground cursor-pointer">
                {cityIssues.length} unresolved project locations
              </summary>
              <ul className="mt-2 max-h-40 space-y-2 overflow-auto">
                {cityIssues.map((issue) => (
                  <li key={issue.id}>
                    {issue.title}: {issue.reason}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
